'use client';

import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { Separator } from '@ahafood/ui/components/separator';
import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';
import { useMemo, useState } from 'react';
import NewStoreSession from './session/new-store-session';
import QuickSearch from './session/quick-search';
import SoloMealSection from './session/solo-meal-section';
import NewStoreBanner from './session/store-banner';
import NewStoreCategory from './session/store-category';
import NewStoreDeal from './session/store-deal';
import StoreQuickFilter from './session/store-quick-filter';
import SimpleStoreList from './simple-store-list';

import DesktopLayout from '@/components/desktop-layout';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { CITIES, useAiFoodOrderStore } from '@/store';

const AmoriContainer = dynamic(
  () => import('@/app/[locale]/amori/amori-container'),
  { ssr: false }
);
const AmoriSidebar = dynamic(
  () => import('@/app/[locale]/amori/amori-sidebar'),
  { ssr: false }
);

interface CityPageClientWrapperProps {
  citySlug: string;
  // Server-provided data for initial render (SSR / hydration)
  initialData?: {
    stores: any[];
    categories?: any[]; // Categories for NewStoreCategory section
    deals?: any[]; // Deals for NewStoreDeal section
  };
}

export default function CityPageClientWrapper({
  citySlug: initialCitySlug,
  initialData,
}: CityPageClientWrapperProps) {
  const isWebinapp = useDetectWebInApp();
  const isMobile = useIsMobile();
  const params = useParams();
  const [isAmoriOpen, setIsAmoriOpen] = useState(false);

  // In webinapp mode, get citySlug from URL params for reactive updates
  // In native web mode, use initialCitySlug from server props
  const citySlug = useMemo(() => {
    if (isWebinapp && params.city) {
      return params.city as string;
    }
    return initialCitySlug;
  }, [isWebinapp, params.city, initialCitySlug]);

  // Get current address from store
  const address = useAiFoodOrderStore.use.address();

  // Find city data - will update when citySlug changes
  const cityData = useMemo(
    () => CITIES.find((c) => c.slug === citySlug),
    [citySlug]
  );

  const cityId = cityData?.value ?? '';

  const mainContent = (
    <>
      {/* Categories Section */}
      <NewStoreCategory city={citySlug} initialData={initialData?.categories} />

      {/* Quick Search Filters */}
      <QuickSearch citySlug={citySlug} />

      <NewStoreBanner />
      <SoloMealSection lat={address?.lat ?? 0} lng={address?.lng ?? 0} />

      <NewStoreDeal
        cityId={cityId}
        citySlug={citySlug}
        initialData={initialData?.deals}
      />

      {/* New Store Section */}
      <NewStoreSession cityId={cityId} citySlug={citySlug} />

      {/* Store List Section */}
      <div
        className="py-8 w-full max-w-screen-xl"
        data-testid="home-store-section"
      >
        <div className="w-full flex items-center justify-center py-6 text-neutral-50 gap-10 bg-transparent mb-4 mx-auto">
          <Separator className="h-px w-full flex-1" />
          <span className="font-semibold" data-testid="home-store-title">
            Tất cả cửa hàng
          </span>
          <Separator className="h-px w-full flex-1" />
        </div>
        <StoreQuickFilter citySlug={citySlug} />
        <SimpleStoreList
          data={initialData?.stores ?? []}
          payload={{ city_id: cityId }}
          processedDeals={[]}
        />
      </div>
    </>
  );

  return (
    <>
      {/* Mobile: ScrollArea with fullscreen Amori */}
      {isMobile ? (
        <>
          <ScrollArea className="mt-[calc(var(--main-layout-header-height,_0px)_*-1)] bg-gradient-to-b from-[#FFF7F2] via-white to-[#EAECEF] [&>div>div]:!block">
            {mainContent}
          </ScrollArea>
          <AmoriContainer isOpen={isAmoriOpen} setOpenChange={setIsAmoriOpen} />
        </>
      ) : (
        /* Desktop: 2-column layout with sidebar */
        <div className="bg-gradient-to-br from-primary-30 via-secondary-20 to-red-20 min-h-screen -mt-32 pt-28">
          <div className="mx-auto max-w-screen-xl">
            <DesktopLayout sidebar={<AmoriSidebar />} className="pt-6">
              {mainContent}
            </DesktopLayout>
          </div>
        </div>
      )}
    </>
  );
}
